import React from 'react';
import { Dialog, DialogContent, Box, TextField, Tooltip, IconButton, Paper, Typography, useTheme, useMediaQuery } from '@mui/material';
import type { GeoJsonFeature } from '../../utils/ogc';
import { DataGrid, type GridColDef, type GridColumnVisibilityModel, type GridRowParams, type GridSortModel } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { format } from 'date-fns';
import { depthToColor } from '../../utils/layer_style';
import RecentlyFloodedStrip from './RecentlyFlooded';


const searchQuery = (p: GeoJsonFeature['properties'], q: string) => {
  const text = `${p.name || ''} ${p.neighborhood || ''} ${p.borough || ''}`.toLowerCase();
  return text.includes(q.toLowerCase());
}

const activeAtTime = (p: GeoJsonFeature['properties'], ref: Date) => {
  return !!(p.date_deployed && p.date_deployed <= ref && (!p.date_down || p.date_down >= ref));
}

const fmtString = (v: string | null) => (v == null || v === '' ? '—' : v);
const fmtDatetime = (v: Date | null) => (v ? format(new Date(v), 'PPpp') : '—');
const fmtDate = (v: Date | null) => (v ? format(new Date(v), 'PP') : '—');
const fmtNum = (v: number | null) => (v == null ? '—' : Number(v).toFixed(1));
const fmtInt = (v: number | null) => (v == null ? '—' : Number(v).toFixed(0));

export default function SensorList({ features, currentTime, onSelect }: { features: GeoJsonFeature[]; currentTime?: Date; onSelect?: (f: GeoJsonFeature) => void }) {
  const [q, setQ] = React.useState('');
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const totalSensors = React.useMemo(() => (features?.length ?? 0), [features]);
  const rows = React.useMemo(() => {
    const list = (features || []) as GeoJsonFeature[];
    const query = q.trim().toLowerCase();
    const filtered = !query ? list : list.filter((f) => searchQuery(f.properties || {}, query));
    return filtered.map((f) => {
      const p = f.properties as GeoJsonFeature['properties'];
      return {
        ...p,
        active_at_time: activeAtTime(p, currentTime ?? new Date()),
        active_now: activeAtTime(p, new Date()),
        feature: f,
      };
    });
  }, [features, q, currentTime]);
  const deployedNowCount = React.useMemo(() => rows.reduce((acc, r) => acc + (r.active_now ? 1 : 0), 0), [rows]);
  const deployedAtTimeCount = React.useMemo(() => rows.reduce((acc, r) => acc + (r.active_at_time ? 1 : 0), 0), [rows]);

  const columns = React.useMemo<GridColDef[]>(() => [
    { field: 'active_at_time', headerName: 'Active', type: 'boolean', width: 100 },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 240, valueFormatter: fmtString },
    { field: 'date_deployed', headerName: 'Deployed on', width: 180, valueFormatter: fmtDate },
    { field: 'date_down', headerName: 'Taken Down on', width: 160, valueFormatter: fmtDate },
    { field: 'last_flood_time', headerName: 'Last Flood', width: 180, valueFormatter: fmtDatetime },
    { field: 'max_depth_inches_of_any_flood', headerName: 'Max Depth of Last Flood (in)', width: 180, type: 'number', valueFormatter: fmtNum },
    { field: 'max_depth_any', headerName: 'Max Depth (in)', width: 180, type: 'number', valueFormatter: fmtNum },
    { field: 'flood_event_count', headerName: '# Floods Observed', width: 180, type: 'number', valueFormatter: fmtInt },
    // { field: 'last_flood_end_time', headerName: 'Last Flood End', width: 180, valueFormatter: fmtDate },
  ], []);

  const isRecent = currentTime && currentTime.getTime() >= Date.now() - 7 * 24 * 3600 * 1000;
  const sortModel = React.useMemo<GridSortModel>(() => ([
    isRecent ? { field: 'last_flood_time', sort: 'desc' } : { field: 'active_at_time', sort: 'desc' }
  ]), [isRecent]);
  const visibilityModel: GridColumnVisibilityModel = React.useMemo(() => {
    return isSmall ? { date_down: false, max_depth_any: false, max_depth_last: false, active_at_time: false } : { active_at_time: false }
  }, [isSmall]);

  const textFieldSearch = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => { setQ(e.target.value); }, []);
  
  const rowSelectAndClose = React.useCallback((params: GridRowParams) => { onSelect?.(params.row.feature); console.log(onSelect, params)}, [onSelect]);
  return (<>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Sensors: <b>{deployedAtTimeCount.toLocaleString()}</b> at {currentTime ? format(currentTime, 'PP') : 'now'} · <b>{deployedNowCount.toLocaleString()}</b> now · <b>{totalSensors.toLocaleString()}</b> all time
          </Typography>
        </Box>
        <Box sx={{ mb: { xs: 1, sm: 2 } }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search all sensors"
            value={q}
            onChange={textFieldSearch}
          />
        </Box>
        <Box sx={{ height: { xs: 'calc(100vh - 170px)', sm: '70vh' }, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            density="compact"
            hideFooterSelectedRowCount
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            columnVisibilityModel={visibilityModel}
            sortModel={sortModel}
            getRowClassName={(params) => (params.row.active_at_time ? '' : 'inactive')}
            onRowClick={rowSelectAndClose}
            sx={{
              '& .MuiDataGrid-columnHeaders': { fontSize: 12, minHeight: { xs: 30, sm: 36 }, maxHeight: { xs: 30, sm: 36 } },
              '& .MuiDataGrid-cell': { fontSize: 12, py: { xs: 0.25, sm: 0.5 } },
              '& .MuiDataGrid-row.inactive .MuiDataGrid-cell': {
                color: 'text.disabled',
              },
            }}
            slots={{
              footer: () => (
                <Box sx={{ px: 1, py: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid', borderColor: 'divider', fontSize: 12 }}>
                  <span>Showing {rows.length.toLocaleString()} of {totalSensors.toLocaleString()} sensors</span>
                  <span>{currentTime ? `At ${format(currentTime, 'PP p')}: ${deployedAtTimeCount.toLocaleString()}` : null} · Now: {deployedNowCount.toLocaleString()}</span>
                </Box>
              )
            }}
          />
        </Box>
  </>)
};